import { daysAgoStr, localDateStr, pad2, todayStr } from "@lifelog/shared";

export type TimeSelectorProps = {
  /** ISO-8601 with offset, or null meaning "now". */
  value: string | null;
  onChange: (iso: string | null) => void;
  maxDaysBack?: number;
};

type DayMode = "today" | "yesterday" | "custom";
type TimeMode = "now" | "custom";

function localTimeStr(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Local-wall-clock date + time → ISO-8601 with offset (the runtime zone). */
export function dateTimeToOffsetIso(dateStr: string, timeStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = (timeStr || "00:00").split(":").map(Number);
  const local = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0, 0, 0);
  const tzMin = -local.getTimezoneOffset();
  const sign = tzMin >= 0 ? "+" : "-";
  const abs = Math.abs(tzMin);
  return `${dateStr}T${pad2(hour ?? 0)}:${pad2(minute ?? 0)}:00${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

function classify(value: string | null): {
  day: DayMode;
  time: TimeMode;
  date: string;
  timeStr: string;
} {
  const now = new Date();
  if (value == null) {
    return { day: "today", time: "now", date: todayStr(), timeStr: localTimeStr(now) };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { day: "today", time: "now", date: todayStr(), timeStr: localTimeStr(now) };
  }
  const date = localDateStr(parsed);
  const timeStr = localTimeStr(parsed);
  if (date === todayStr()) return { day: "today", time: "custom", date, timeStr };
  if (date === daysAgoStr(1)) return { day: "yesterday", time: "custom", date, timeStr };
  return { day: "custom", time: "custom", date, timeStr };
}

const pillWrap =
  "flex rounded-full bg-(--accent)/15 p-0.5 text-xs font-medium";
const pillOn =
  "rounded-full px-4 py-1 bg-white text-(--accent) shadow-sm dark:bg-(--accent) dark:text-(--accent-contrast)";
const pillOff =
  "rounded-full px-4 py-1 text-(--accent) hover:text-gray-900 dark:hover:text-white";
const dateInputCls =
  "rounded-md border border-(--accent)/40 bg-white px-2 py-0.5 text-xs text-gray-800 " +
  "focus:outline-none focus:ring-2 focus:ring-(--accent) dark:bg-gray-950 dark:text-gray-200";

export function TimeSelector({ value, onChange, maxDaysBack }: TimeSelectorProps) {
  const { day, time, date, timeStr } = classify(value);
  const minDate = maxDaysBack != null ? daysAgoStr(maxDaysBack) : undefined;
  const maxDate = todayStr();

  function selectDay(next: DayMode) {
    if (next === "today") {
      if (time === "now" || value == null) {
        onChange(null);
        return;
      }
      onChange(dateTimeToOffsetIso(todayStr(), timeStr));
      return;
    }
    if (next === "yesterday") {
      onChange(dateTimeToOffsetIso(daysAgoStr(1), timeStr));
      return;
    }
    onChange(dateTimeToOffsetIso(date === todayStr() || date === daysAgoStr(1) ? daysAgoStr(2) : date, timeStr));
  }

  function selectTime(next: TimeMode) {
    if (next === "now") {
      onChange(null);
      return;
    }
    onChange(dateTimeToOffsetIso(todayStr(), timeStr));
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className={pillWrap} role="group" aria-label="Day">
        <button type="button" onClick={() => selectDay("today")} className={day === "today" ? pillOn : pillOff}>
          Today
        </button>
        {(maxDaysBack == null || maxDaysBack >= 1) && (
          <button
            type="button"
            onClick={() => selectDay("yesterday")}
            className={day === "yesterday" ? pillOn : pillOff}
          >
            Yesterday
          </button>
        )}
        <button type="button" onClick={() => selectDay("custom")} className={day === "custom" ? pillOn : pillOff}>
          Custom
        </button>
      </div>

      {day === "today" && (
        <div className={pillWrap} role="group" aria-label="Time">
          <button type="button" onClick={() => selectTime("now")} className={time === "now" ? pillOn : pillOff}>
            Now
          </button>
          <button type="button" onClick={() => selectTime("custom")} className={time === "custom" ? pillOn : pillOff}>
            Custom
          </button>
        </div>
      )}

      {day === "custom" && (
        <div className="flex gap-1.5">
          <input
            type="date"
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => onChange(dateTimeToOffsetIso(e.target.value || todayStr(), timeStr))}
            className={dateInputCls}
          />
          <input
            type="time"
            value={timeStr}
            onChange={(e) => onChange(dateTimeToOffsetIso(date, e.target.value || "00:00"))}
            className={dateInputCls}
          />
        </div>
      )}

      {day === "yesterday" && (
        <input
          type="time"
          value={timeStr}
          onChange={(e) => onChange(dateTimeToOffsetIso(daysAgoStr(1), e.target.value || "00:00"))}
          className={dateInputCls}
        />
      )}

      {day === "today" && time === "custom" && (
        <input
          type="time"
          value={timeStr}
          onChange={(e) => onChange(dateTimeToOffsetIso(todayStr(), e.target.value || "00:00"))}
          className={dateInputCls}
        />
      )}
    </div>
  );
}
